import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../helpers'
import { useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'

interface PaginationParams {
    limit: number
    offset: number
    sort?: string
    workspaceId?: number | undefined
    archived?: boolean
    organizationId?: number
    search?: string
    tagIds?: number[]
}

interface PaginatedWorkspaceProjectResponse {
    results: WorkspaceProjectsAnalytics[]
    count: number
}

interface PaginatedProjectAnalyticsResponse {
    results: ProjectAnalytics[]
    count: number
}

export interface WorkspaceProjectsAnalytics {
    id: number
    apartment_name: string
    total_created: number
    active_count: number
    behind_count: number
    most_recent_date: string | null
    future_count: number
    total_active_schedules: number
    future_schedules: number
}

export interface ProjectAnalytics {
    id: number
    name: string
    start_date: string
    end_date: string
    total_workorders: number
    behind_count: number
    not_started: number
    in_progress_count: number
    complete_count: number
    approved_count: number
    invoiced_count: number
    invoice_request_count: number
    invoiced_out_count: number
    cai_count: number
    active: boolean
    unassigned_occupiable_units: number
    unique_occupiable_areas: number // Total number of occupiable areas with a workorder for this project/service
    unique_units: number // total number of units with a workorder for this project/service
    total_cost: number
    cost_per_unit: number
    cost_per_occupiable_area: number
    bed_spaces: number
    baseline_budget: number
    forecasted_budget: number
    budget_variance: number
    approved_change_orders: number
    approved_change_order_cost: number
    pending_change_orders: number
    pending_change_order_cost: number
    actions: string
    apartment_id: number
    apartment_name: string
    units_ready: number
    units_ready_count?: number
    units_ready_scope_count?: number
    units_scheduled: number
    unassigned_units: number
    units_assigned: number
    units_need_service: number
    units_with_pending_work: number
    concern_level: 'LOW' | 'MEDIUM' | 'HIGH'
    property_unit_count?: number | null
    turn_eligible_unit_count?: number | null
    unit_turn_pct?: number | null
    max_vendor_output?: number | null
    max_vendor_output_service_name?: string | null
    max_vendor_output_vendor_id?: number | null
}

// Separate pagination state for each data type
interface PaginationState {
    limit: number
    offset: number
    sort?: string
}

// Hook for workspace project analytics only
export const useWorkspaceProjectAnalytics = ({
    limit,
    offset,
    sort,
    workspaceId,
    organizationId,
    tagIds = [],
}: PaginationParams): {
    workspaceProjectAnalytics: any
    workspaceProjectAnalyticsLoading: boolean
    refetchWorkspaceProjectAnalytics: () => void
    totalCount: number
    numPages: number
    currentPage: number
    start: number
    end: number
    pageSize: number
    goToPage: (page: number) => void
    nextPage: () => void
    prevPage: () => void
    changePageSize: (newLimit: number) => void
    pagination: PaginationState
} => {
    const queryClient = useQueryClient()

    const [pagination, setPagination] = useState<PaginationState>({
        limit,
        offset,
        sort,
    })

    // Update pagination state when props change
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            limit,
            offset,
            sort,
        }))
    }, [limit, offset, sort, organizationId])

    // Reset pagination when organizationId changes
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            offset: 0, // Reset to first page when organization changes
        }))
    }, [organizationId])

    const tagIdsKey = tagIds.join(',')

    // Reset pagination when tag filter changes
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            offset: 0,
        }))
    }, [tagIdsKey])

    const queryKey = [
        'workspace-project-analytics',
        pagination.limit,
        pagination.offset,
        pagination.sort,
        organizationId,
        tagIdsKey,
    ]

    const {
        data: workspaceProjectAnalytics,
        isLoading: workspaceProjectAnalyticsLoading,
        refetch: refetchWorkspaceProjectAnalytics,
    } = useQuery<AxiosResponse<PaginatedWorkspaceProjectResponse>, Error>({
        queryKey,
        queryFn: () => {
            return axiosInstance.get(
                '/workorder/org-project-analytics/workspace-project-analytics/',
                {
                    params: {
                        limit: pagination.limit,
                        offset: pagination.offset,
                        ...(pagination.sort && { sort: pagination.sort }),
                        ...(workspaceId && { workspace_id: workspaceId }),
                        ...(organizationId && {
                            organization_id: organizationId,
                        }),
                        ...(tagIds.length > 0 && {
                            tag_ids: tagIds.join(','),
                        }),
                    },
                },
            )
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    // Pagination control functions
    const goToPage = (page: number) => {
        const newOffset = (page - 1) * pagination.limit
        setPagination((prev) => ({
            ...prev,
            offset: newOffset,
        }))
    }

    const nextPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: prev.offset + prev.limit,
        }))
    }

    const prevPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: Math.max(0, prev.offset - prev.limit),
        }))
    }

    const changePageSize = (newLimit: number) => {
        setPagination((prev) => ({
            ...prev,
            limit: newLimit,
            offset: 0, // Reset to first page when changing page size
        }))
    }

    // Calculate pagination values
    const totalCount = workspaceProjectAnalytics?.data.count ?? 0
    const numPages = Math.ceil(totalCount / pagination.limit)
    const currentPage = Math.floor(pagination.offset / pagination.limit) + 1
    const start = pagination.offset + 1
    const end = Math.min(pagination.offset + pagination.limit, totalCount)

    return {
        workspaceProjectAnalytics,
        workspaceProjectAnalyticsLoading,
        refetchWorkspaceProjectAnalytics,
        // Pagination values
        totalCount,
        numPages,
        currentPage,
        start,
        end,
        pageSize: pagination.limit,
        // Pagination controls
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
        // Current pagination state
        pagination,
    }
}

// Hook for project analytics only
export const useProjectAnalytics = ({
    limit,
    offset,
    sort,
    workspaceId,
    archived,
    organizationId,
    search,
    tagIds = [],
}: PaginationParams) => {
    const queryClient = useQueryClient()

    const [pagination, setPagination] = useState<PaginationState>({
        limit,
        offset,
        sort,
    })

    const [currentWorkspaceId, setCurrentWorkspaceId] = useState<
        number | undefined
    >(workspaceId)

    // Update workspaceId when prop changes
    useEffect(() => {
        setCurrentWorkspaceId(workspaceId)
    }, [workspaceId])

    // Update pagination state when props change
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            limit,
            offset,
            sort,
        }))
    }, [limit, offset, sort, organizationId])

    // Reset pagination when organizationId changes
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            offset: 0, // Reset to first page when organization changes
        }))
    }, [organizationId])

    const tagIdsKey = tagIds.join(',')
    const searchKey = search?.trim() ?? ''

    // Reset pagination when search or tag filter changes
    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            offset: 0,
        }))
    }, [searchKey, tagIdsKey])

    const queryKey = [
        'project-analytics',
        pagination.limit,
        pagination.offset,
        pagination.sort,
        workspaceId,
        archived,
        organizationId,
        searchKey,
        tagIdsKey,
    ]

    const {
        data: projectAnalytics,
        isLoading: projectAnalyticsLoading,
        refetch: refetchProjectAnalytics,
    } = useQuery<AxiosResponse<PaginatedProjectAnalyticsResponse>, Error>({
        queryKey,
        queryFn: () => {
            return axiosInstance.get(
                '/workorder/org-project-analytics/project-analytics/',
                {
                    params: {
                        limit: pagination.limit,
                        offset: pagination.offset,
                        ...(pagination.sort && { sort: pagination.sort }),
                        ...(workspaceId && { workspace_id: workspaceId }),
                        ...(archived && { archived: archived }),
                        ...(organizationId && {
                            organization_id: organizationId,
                        }),
                        ...(searchKey && { search: searchKey }),
                        ...(tagIds.length > 0 && {
                            tag_ids: tagIds.join(','),
                        }),
                    },
                },
            )
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    // Function to update workspaceId
    const setWorkspaceId = (newWorkspaceId: number | undefined) => {
        setCurrentWorkspaceId(newWorkspaceId)
        // Reset pagination when changing workspace
        setPagination((prev) => ({
            ...prev,
            offset: 0,
        }))
    }

    // Pagination control functions
    const goToPage = (page: number) => {
        const newOffset = (page - 1) * pagination.limit
        setPagination((prev) => ({
            ...prev,
            offset: newOffset,
        }))
    }

    const nextPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: prev.offset + prev.limit,
        }))
    }

    const prevPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: Math.max(0, prev.offset - prev.limit),
        }))
    }

    const changePageSize = (newLimit: number) => {
        setPagination((prev) => ({
            ...prev,
            limit: newLimit,
            offset: 0, // Reset to first page when changing page size
        }))
    }

    // Calculate pagination values
    const totalCount = projectAnalytics?.data.count ?? 0
    const numPages = Math.ceil(totalCount / pagination.limit)
    const currentPage = Math.floor(pagination.offset / pagination.limit) + 1
    const start = pagination.offset + 1
    const end = Math.min(pagination.offset + pagination.limit, totalCount)

    return {
        projectAnalytics,
        projectAnalyticsLoading,
        refetchProjectAnalytics,
        // Pagination values
        totalCount,
        numPages,
        currentPage,
        start,
        end,
        pageSize: pagination.limit,
        // Pagination controls
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
        // Current pagination state
        pagination,
        setWorkspaceId,
    }
}

export const useServiceLevelAnalytics = ({
    projectId,
}: {
    projectId: number
}) => {
    const {
        data: serviceLevelAnalytics,
        isLoading: serviceLevelAnalyticsLoading,
    } = useQuery<AxiosResponse<ProjectAnalytics[]>, Error>({
        queryKey: ['service-level-analytics', projectId],
        queryFn: () =>
            axiosInstance.get(
                `/workorder/org-project-analytics/service-level-analytics/`,
                {
                    params: {
                        project_id: projectId,
                    },
                },
            ),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })
    return { serviceLevelAnalytics, serviceLevelAnalyticsLoading }
}

interface VendorPaginationParams {
    vendorId: number
    limit: number
    offset: number
    sort?: string
    workspaceId?: number
    organizationId?: number
    archived?: boolean
}

export type VendorProjectAnalytics = Omit<
    ProjectAnalytics,
    | 'concern_level'
    | 'baseline_budget'
    | 'forecasted_budget'
    | 'budget_variance'
> & {
    total_sub_payout: number
    profit: number
}

interface PaginatedVendorProjectAnalyticsResponse {
    results: VendorProjectAnalytics[]
    count: number
}

export const useVendorProjectAnalytics = ({
    vendorId,
    limit,
    offset,
    sort,
    workspaceId,
    organizationId,
    archived,
}: VendorPaginationParams) => {
    const [pagination, setPagination] = useState<PaginationState>({
        limit,
        offset,
        sort,
    })

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            limit,
            offset,
            sort,
        }))
    }, [limit, offset, sort])

    const queryKey = [
        'vendor-project-analytics',
        vendorId,
        pagination.limit,
        pagination.offset,
        pagination.sort,
        workspaceId,
        archived,
        organizationId,
    ]

    const {
        data: projectAnalytics,
        isLoading: projectAnalyticsLoading,
        refetch: refetchProjectAnalytics,
    } = useQuery<AxiosResponse<PaginatedVendorProjectAnalyticsResponse>, Error>(
        {
            queryKey,
            queryFn: () =>
                axiosInstance.get(
                    `/company/vendor/${vendorId}/project-analytics/`,
                    {
                        params: {
                            limit: pagination.limit,
                            offset: pagination.offset,
                            ...(pagination.sort && { sort: pagination.sort }),
                            ...(workspaceId && { workspace_id: workspaceId }),
                            ...(archived !== undefined && { archived }),
                            ...(organizationId && {
                                organization_id: organizationId,
                            }),
                        },
                    },
                ),
            enabled: !!vendorId,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        },
    )

    const goToPage = (page: number) => {
        setPagination((prev) => ({
            ...prev,
            offset: (page - 1) * prev.limit,
        }))
    }

    const nextPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: prev.offset + prev.limit,
        }))
    }

    const prevPage = () => {
        setPagination((prev) => ({
            ...prev,
            offset: Math.max(0, prev.offset - prev.limit),
        }))
    }

    const changePageSize = (newLimit: number) => {
        setPagination((prev) => ({
            ...prev,
            limit: newLimit,
            offset: 0,
        }))
    }

    const totalCount = projectAnalytics?.data.count ?? 0
    const numPages = Math.ceil(totalCount / pagination.limit)
    const currentPage = Math.floor(pagination.offset / pagination.limit) + 1
    const start = pagination.offset + 1
    const end = Math.min(pagination.offset + pagination.limit, totalCount)

    return {
        projectAnalytics,
        projectAnalyticsLoading,
        refetchProjectAnalytics,
        totalCount,
        numPages,
        currentPage,
        start,
        end,
        pageSize: pagination.limit,
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
    }
}

export type VendorProfitTrends = Record<
    string,
    { income: number; expenses: number }
>

interface VendorProfitTrendsParams {
    vendorId: number
    archived?: boolean
    organizationId?: number
    startDate?: string
    endDate?: string
    enabled?: boolean
}

export const useVendorProfitTrends = ({
    vendorId,
    archived,
    organizationId,
    startDate,
    endDate,
    enabled = true,
}: VendorProfitTrendsParams) => {
    const hasValidDateRange = !startDate || !endDate || startDate <= endDate

    const { data, isLoading } = useQuery<
        AxiosResponse<VendorProfitTrends>,
        Error
    >({
        queryKey: [
            'vendor-profit-trends',
            vendorId,
            archived,
            organizationId,
            startDate,
            endDate,
        ],
        queryFn: () =>
            axiosInstance.get(`/company/vendor/${vendorId}/profit-trends/`, {
                params: {
                    ...(archived !== undefined && { archived }),
                    ...(organizationId && {
                        organization_id: organizationId,
                    }),
                    ...(startDate && { start_date: startDate }),
                    ...(endDate && { end_date: endDate }),
                },
            }),
        enabled: !!vendorId && enabled && hasValidDateRange,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        trends: data?.data ?? {},
        trendsLoading: isLoading,
    }
}

// Combined hook for both (if you need both at the same time)
export const useEZFolioProject = ({
    limit,
    offset,
    sort,
    workspaceId,
    organizationId,
}: PaginationParams) => {
    const workspaceProjectHook = useWorkspaceProjectAnalytics({
        limit,
        offset,
        sort,
        workspaceId,
        organizationId,
    })

    const projectAnalyticsHook = useProjectAnalytics({
        limit,
        offset: 0, // Start from beginning for project analytics
        sort,
        workspaceId,
        organizationId,
    })

    return {
        // Workspace project analytics
        ...workspaceProjectHook,
        // Project analytics
        ...projectAnalyticsHook,
    }
}
